import { useEffect, useState } from "react";
import {
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Input,
  Radio,
  RadioChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "antd";

import {
  COMPANY_ROLES_SELECT,
  CompanyType,
  EditCompanyRequest,
  COMPANY_TYPES,
  COMPANY_TYPE_LABELS,
  CompanyStatus,
  COMPANY_STATUS_ENUM,
  BankDto,
} from "interfaces";
import {
  DefaultInput,
  CustomButton,
  DefaultSelect,
  WhiteBoxBlock,
  BankSelect,
  NumberInput,
  DefaultDateInput,
  PhoneNumberInput,
} from "ui";
import {
  _isRequired,
  _isNotLongerThan,
  _isOnlyNumbersWithLength,
  renderSelectOptions,
  renderRadioButtons,
  _isRegex,
  _isEmail,
  _isPhoneNumber,
  _isLengthEqual,
  _isCorrectCompanyName,
  _isLengthBetween,
  _isToggleChecked,
  _isCorrectAddress,
  _isCorrectName,
  _isPassportCorrectValue,
  _isRequiredDate,
  codeIssuerMask,
} from "utils";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import style from "./companyForm.module.css";

import { DIRECT_PAYMENT_CUSTOMER_LP, SAFE_DEAL } from "utils/envConstants";
import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { ApproveSafeDealFormBtn } from "./ApproveSafeDealFormBtn";
import dayjs from "dayjs";

interface IProps {
  form: FormInstance<any>;
  hideStatementBlock?: boolean;
  disabled: boolean;
  initValues?: EditCompanyRequest;
  companyStatus?: CompanyStatus;
  onFinish: () => Promise<void>;
  closeHandler: () => Promise<void>;
  editMode?: boolean;
}

export const CompanyForm = observer(
  ({ disabled, form, initValues, onFinish, closeHandler, hideStatementBlock, companyStatus, editMode }: IProps) => {
    const { user, companyCard } = useStores();
    const [companyType, setCompanyType] = useState<CompanyType>(initValues?.type || COMPANY_TYPES.COMPANY);
    const [isResident, setIsResident] = useState<boolean>(initValues?.isResident || true);
    const { isSeniorCustomer, isJuniorOperator, isUserAdmin, isSeniorOperator } = user.roles;

    const isNotDraftCompany = companyStatus ? companyStatus !== COMPANY_STATUS_ENUM.DRAFT : false;
    const isActiveCompany = companyStatus ? companyStatus === COMPANY_STATUS_ENUM.ACTIVE : false;

    const [isNewSelect, setNewSelect] = useState(false);

    const disabledFieldsForSafeDeal = isActiveCompany && companyCard.card.beneficiaryCreated && isUserAdmin;

    // const isBtnApproveSafeDealFormVisible =
    //   isUserAdmin &&
    //   companyCard.card.beneficiaryCreated &&
    //   editMode &&
    //   isActiveCompany &&
    //   SAFE_DEAL &&
    //   !DIRECT_PAYMENT_CUSTOMER_LP;

    const handleChangeCompanyType = (e: RadioChangeEvent) => {
      setCompanyType(e.target.value);
      const values = form.getFieldsValue();
      if (values.inn) {
        form.validateFields(["inn"]);
      }
      if (values.companyRegistrationNumber) {
        form.validateFields(["companyRegistrationNumber"]);
      }
    };

    const onBankDataReceived = (bankData: BankDto & { bik: string }) => {
      form.setFieldsValue({
        bankAccountRequest: {
          bankName: bankData.bankName,
          bankRegistrationNumber: bankData.bankRegistrationNumber,
          correspondentAccount: isNewSelect ? bankData.correspondentAccount : initValues?.correspondentAccount,
        },
      });

      form.validateFields([
        ["bankAccountRequest", "correspondentAccount"],
        ["bankAccountRequest", "bankRegistrationNumber"],
        ["bankAccountRequest", "bankName"],
      ]);
    };

    const contractNumberRegex = /^[А-ЯЁа-яеA-Za-z\d\/\-\_]+$/;
    const codeRegex = /^\d{3}-\d{3}$/;
    const registryNumberForIpRegex = /^3[0-9]{14}$/;
    const identificationDocIssuerRegex = /^[А-ЯЁа-яе0-9 -/.№]{0,150}$/;

    const innText = companyType === COMPANY_TYPES.COMPANY ? "ИНН компании" : "ИНН";

    useEffect(() => {
      !editMode && form.resetFields();
    }, [companyType]);

    return (
      <Form
        form={form}
        name="createCompany"
        initialValues={{
          type: companyType,
          isResident: isResident,
          ...initValues,
        }}
        onFinish={onFinish}
      >
        <WhiteBoxBlock>
          <Flex vertical>
            {!isActiveCompany ? (
              <Flex gap="large">
                <Form.Item
                  rules={!isActiveCompany ? [_isRequired("Роль обязательна для ввода")] : undefined}
                  className="full-width"
                  name={!isActiveCompany ? "role" : undefined}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  initialValue={initValues?.role}
                  children={
                    <DefaultSelect
                      placeholder="Роль"
                      disabled={disabled || isActiveCompany}
                      size="large"
                      defaultValue={initValues?.role}
                    >
                      {renderSelectOptions(COMPANY_ROLES_SELECT)}
                    </DefaultSelect>
                  }
                />
              </Flex>
            ) : (
              <Flex vertical gap="large">
                <Form.Item className="full-width" label="">
                  <DefaultSelect placeholder="Роль" disabled={true} size="large" value={initValues?.role}>
                    {renderSelectOptions(COMPANY_ROLES_SELECT)}
                  </DefaultSelect>
                </Form.Item>
              </Flex>
            )}

            <Flex className="margin-bottom-lg" gap="large" vertical>
              <Form.Item name="type" noStyle>
                <Radio.Group
                  name="type"
                  optionType="button"
                  buttonStyle="solid"
                  className="radio-group"
                  disabled={disabled || isNotDraftCompany || disabledFieldsForSafeDeal}
                  onChange={handleChangeCompanyType}
                >
                  {renderRadioButtons(COMPANY_TYPE_LABELS, style.companyRadioBtn)}
                </Radio.Group>
              </Form.Item>

              {/*BEGIN: Свитчи для режима SAFE_DEAL */}
              {companyType === COMPANY_TYPES.COMPANY ? (
                <Flex gap={24} style={{ height: "36px" }}>
                  <Form.Item label="" className={style.errorText}>
                    <Tooltip title="Юр. лицо зарегистрировано в РФ и является налоговым резидентом РФ">
                      <Flex gap="small" align="center">
                        <Form.Item
                          name="isResident"
                          noStyle
                          rules={SAFE_DEAL ? [_isToggleChecked("Не разрешено в рамках ЭПР", true)] : []}
                        >
                          <Switch disabled={disabledFieldsForSafeDeal} />
                        </Form.Item>
                        <span>Юр. лицо - резидент РФ</span>
                      </Flex>
                    </Tooltip>
                  </Form.Item>

                  <Form.Item label="" className={style.errorText}>
                    <Tooltip title="Руководитель является налоговым резидентом РФ">
                      <Flex gap="small" align="center">
                        <Form.Item
                          name="isControlPersonsRF"
                          noStyle
                          rules={SAFE_DEAL ? [_isToggleChecked("Не разрешено в рамках ЭПР", true)] : []}
                        >
                          <Switch disabled={disabledFieldsForSafeDeal} />
                        </Form.Item>
                        <span>Руководство - резиденты РФ</span>
                      </Flex>
                    </Tooltip>
                  </Form.Item>

                  <Form.Item label="" className={style.errorText}>
                    <Tooltip title="Все учредители юр. лица являются резидентами РФ">
                      <Flex gap="small" align="center">
                        <Form.Item
                          name="isOnlyTaxResidentRF"
                          noStyle
                          rules={SAFE_DEAL ? [_isToggleChecked("Не разрешено в рамках ЭПР", true)] : []}
                        >
                          <Switch disabled={disabledFieldsForSafeDeal} />
                        </Form.Item>
                        <span>Учредители - резиденты РФ</span>
                      </Flex>
                    </Tooltip>
                  </Form.Item>

                  <Form.Item label="" className={style.errorText}>
                    <Tooltip title="Юр. лицо зарегистрировано за пределами РФ">
                      <Flex gap="small" align="center">
                        <Form.Item
                          name="isForeignOrg"
                          noStyle
                          rules={SAFE_DEAL ? [_isToggleChecked("Не разрешено в рамках ЭПР", false)] : []}
                        >
                          <Switch disabled={disabledFieldsForSafeDeal} />
                        </Form.Item>
                        <span>Иностранная организация</span>
                      </Flex>
                    </Tooltip>
                  </Form.Item>
                </Flex>
              ) : (
                <Flex gap={24} style={{ height: "36px" }}>
                  <Form.Item label="" className={style.errorText}>
                    <Tooltip title="ФЛ является налоговым резидентом РФ">
                      <Flex gap="small" align="center">
                        <Form.Item
                          name="isOnlyTaxResidentRF"
                          noStyle
                          rules={SAFE_DEAL ? [_isToggleChecked("Не разрешено в рамках ЭПР", true)] : []}
                        >
                          <Switch disabled={disabledFieldsForSafeDeal} />
                        </Form.Item>
                        <span>Налоговый резидент РФ</span>
                      </Flex>
                    </Tooltip>
                  </Form.Item>
                </Flex>
              )}

              {/*END: Свитчи для режима SAFE_DEAL */}
            </Flex>

            <Flex
              gap={companyType === COMPANY_TYPES.COMPANY ? "small" : "0"}
              wrap={companyType !== COMPANY_TYPES.COMPANY ? true : false}
            >
              {companyType === COMPANY_TYPES.COMPANY ? (
                <Form.Item
                  name="companyName"
                  rules={[_isCorrectCompanyName(), _isLengthBetween(3, 160)]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  className="full-width"
                >
                  <DefaultInput placeholder="Название компании" disabled={disabled} maxLength={160} />
                </Form.Item>
              ) : (
                <Flex className="full-width" vertical>
                  {/*BEGIN: ФИО */}
                  <Flex gap="small">
                    <Form.Item className="full-width" rules={[_isCorrectName()]} name="surname">
                      <DefaultInput
                        placeholder="Фамилия"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={50}
                      />
                    </Form.Item>
                    <Form.Item className="full-width" rules={[_isCorrectName()]} name="name">
                      <DefaultInput placeholder="Имя" disabled={disabled || disabledFieldsForSafeDeal} maxLength={50} />
                    </Form.Item>
                    <Form.Item className="full-width" rules={[_isCorrectName({ required: false })]} name="patronymic">
                      <DefaultInput
                        placeholder="Отчество"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={50}
                      />
                    </Form.Item>
                    <Form.Item
                      name="birthDate"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      rules={[_isRequiredDate()]}
                    >
                      <DefaultDateInput
                        placeholder="Дата рождения"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        minDate={dayjs("1900-01-01")}
                      />
                    </Form.Item>
                  </Flex>
                  {/*END: ФИО */}

                  {/*BEGIN: Паспортные данные */}
                  <Flex gap="small">
                    <Form.Item
                      name="identificationDocSeria"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      style={{ flex: 1 }}
                      rules={[_isPassportCorrectValue({ length: 4 })]}
                    >
                      <NumberInput
                        placeholder="Серия паспорта"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={4}
                      />
                    </Form.Item>
                    <Form.Item
                      name="identificationDocNumber"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      style={{ flex: 2 }}
                      rules={[_isPassportCorrectValue({ length: 6 })]}
                    >
                      <NumberInput
                        placeholder="Номер паспорта"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={6}
                      />
                    </Form.Item>
                    <Form.Item
                      name="identificationDocIssuer"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      style={{ flex: 3 }}
                      rules={[
                        _isRegex({
                          regex: identificationDocIssuerRegex,
                          error: "Неверный формат",
                          required: true,
                        }),
                      ]}
                    >
                      <DefaultInput
                        placeholder="Кем выдан"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={150}
                      />
                    </Form.Item>
                    <Form.Item
                      name="identificationDocIssuerCode"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      style={{ flex: 2 }}
                      rules={[
                        _isPassportCorrectValue({
                          length: 7,
                          regex: codeRegex,
                          error: "Неверный формат кода подразделения 777-777",
                        }),
                      ]}
                    >
                      <DefaultInput
                        placeholder="Код подразделения"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        maxLength={7}
                        mask={codeIssuerMask}
                      />
                    </Form.Item>
                    <Form.Item
                      name="identificationDocIssueDate"
                      className="full-width"
                      validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      style={{ flex: 2 }}
                      rules={[_isRequiredDate()]}
                    >
                      <DefaultDateInput
                        placeholder="Дата выдачи"
                        disabled={disabled || disabledFieldsForSafeDeal}
                        minDate={dayjs("1900-01-01")}
                      />
                    </Form.Item>
                  </Flex>
                  {/*END: Паспортные данные */}
                </Flex>
              )}

              <Form.Item
                name={companyType === COMPANY_TYPES.COMPANY ? "address" : "postAddress"}
                rules={[_isNotLongerThan(255), _isCorrectAddress()]}
                className="full-width"
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <DefaultInput
                  placeholder={companyType === COMPANY_TYPES.COMPANY ? "Юридический адрес" : "Почтовый адрес"}
                  disabled={disabled || disabledFieldsForSafeDeal}
                  maxLength={255}
                />
              </Form.Item>
            </Flex>

            {/*BEGIN: Поля для режима SAFE_DEAL */}
            <Flex gap="small">
              <Form.Item
                name="contractNumber"
                rules={
                  SAFE_DEAL
                    ? [
                        _isRegex({
                          regex: contractNumberRegex,
                          error: "Неверный формат номера договора-основания",
                          required: true,
                        }),
                      ]
                    : [
                        _isRegex({
                          regex: contractNumberRegex,
                          error: "Неверный формат номера договора-основания",
                          required: false,
                        }),
                      ]
                }
                className="full-width"
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <DefaultInput
                  placeholder="Номер договора-основания"
                  disabled={disabled || disabledFieldsForSafeDeal}
                  maxLength={25}
                />
              </Form.Item>

              <Form.Item
                name="contractDate"
                rules={SAFE_DEAL ? [_isRequired("Дата заключения обязательна для ввода")] : []}
                className="full-width"
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <DefaultDateInput
                  placeholder="Дата заключения"
                  disabled={disabled || disabledFieldsForSafeDeal}
                  minDate={dayjs("2000-01-01")}
                />
              </Form.Item>
            </Flex>

            <Flex gap="small">
              <PhoneNumberInput
                name="phone"
                placeholder="Номер телефона"
                rules={SAFE_DEAL ? [_isRequired("Номер телефона обязателен для ввода")] : []}
              />
              <Form.Item
                name="email"
                rules={SAFE_DEAL ? [_isRequired("Email обязателен для ввода"), _isEmail()] : [_isEmail()]}
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
                className="full-width"
              >
                <DefaultInput placeholder="Email" disabled={disabled} maxLength={40} />
              </Form.Item>
            </Flex>
            {/*END: Поля для режима SAFE_DEAL */}

            <Flex gap="small">
              <Form.Item
                name="companyRegistrationNumber"
                rules={
                  companyType === COMPANY_TYPES.COMPANY
                    ? [_isRequired("ОГРН обязателен для ввода"), _isOnlyNumbersWithLength(13)]
                    : [
                        _isRegex({
                          regex: registryNumberForIpRegex,
                          error: "ОГРНИП состоит из 15 цифр и начинается с цифры 3",
                          required: true,
                        }),
                      ]
                }
                className="full-width"
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <NumberInput
                  placeholder={companyType === "COMPANY" ? "ОГРН" : "ОГРНИП"}
                  disabled={disabled || disabledFieldsForSafeDeal}
                  maxLength={companyType === "COMPANY" ? 13 : 15}
                />
              </Form.Item>
              <Form.Item
                name="inn"
                rules={[
                  _isRequired(`${innText} обязателен для ввода`),
                  _isOnlyNumbersWithLength(companyType === "COMPANY" ? 10 : 12),
                ]}
                className="full-width"
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <NumberInput
                  placeholder={innText}
                  disabled={disabled || disabledFieldsForSafeDeal}
                  maxLength={companyType === "COMPANY" ? 10 : 12}
                />
              </Form.Item>
              {companyType === COMPANY_TYPES.COMPANY && (
                <Form.Item
                  name="kpp"
                  rules={[_isRequired("КПП обязателен для ввода"), _isOnlyNumbersWithLength(9)]}
                  className="full-width"
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                >
                  <NumberInput placeholder="КПП" disabled={disabled || disabledFieldsForSafeDeal} maxLength={9} />
                </Form.Item>
              )}
            </Flex>

            {hideStatementBlock && (
              <>
                <Typography.Text className="margin-bottom-xs" strong>
                  Банковские реквизиты
                </Typography.Text>

                <BankSelect
                  inputProps={{
                    disabled,
                    defaultValue: initValues?.initialBankBik || "",
                    onSelect: () => setNewSelect(true),
                  }}
                  name={["bankAccountRequest", "bik"]}
                  onBankDataReceived={onBankDataReceived}
                />

                <Form.Item name={["bankAccountRequest", "bankName"]} hidden />

                <Form.Item
                  name={["bankAccountRequest", "bankRegistrationNumber"]}
                  rules={[_isRequired("Регистрационный номер КГРКО обязателен. Для заполнения поля выберите банк")]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  className="full-width"
                >
                  <DefaultInput placeholder="Регистрационный номер КГРКО" disabled />
                </Form.Item>
                <Form.Item
                  name={["bankAccountRequest", "correspondentAccount"]}
                  rules={[_isRequired("Корреспондентский счет обязателен для ввода"), _isOnlyNumbersWithLength(20)]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  className="full-width"
                >
                  <NumberInput placeholder="Корреспондентский счет" disabled={disabled} maxLength={20} />
                </Form.Item>
                <Form.Item
                  name={["bankAccountRequest", "accountNumber"]}
                  rules={[_isRequired("Расчетный счет обязателен для ввода"), _isOnlyNumbersWithLength(20)]}
                  validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  className="full-width"
                >
                  <NumberInput placeholder="Расчетный счет" disabled={disabled} maxLength={20} />
                </Form.Item>
              </>
            )}
          </Flex>
        </WhiteBoxBlock>
        <Form.Item>
          <Flex gap="middle" className="margin-top-lg">
            <CustomButton disabled={disabled} loading={disabled} htmlType="submit">
              Сохранить
            </CustomButton>
            {/* {isBtnApproveSafeDealFormVisible && (
              <ApproveSafeDealFormBtn disabled={disabled} loading={disabled} onFinish={onFinish} />
            )} */}
            <CustomButton
              variant="outlined"
              disabled={disabled}
              loading={disabled}
              htmlType="button"
              onClick={closeHandler}
            >
              Отмена
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    );
  }
);
